import React from 'react';
import { useTranslatedContent } from '../../../i18n/hooks/useTranslatedContent';
import { Video, Users, Shield, Globe, Presentation, MessageSquare } from 'lucide-react';
import Button from '../../common/Button';

const MeetingSolution = () => {
  const { t } = useTranslatedContent('meeting');

  return (
    <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 rounded-3xl p-8 lg:p-12 text-white">
      <div className="text-center mb-12">
        <h3 className="text-2xl font-semibold">{t('title')}</h3>
        <p className="mt-4 text-blue-100">{t('subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* BigBlueButton */}
        <div className="relative group">
          <div className="absolute inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
          <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
            <div className="flex items-center mb-6">
              <Presentation className="h-8 w-8 text-blue-400 mr-3" />
              <h4 className="text-xl font-semibold text-white">{t('solutions.bbb.title')}</h4>
            </div>
            <p className="text-blue-100 mb-6">{t('solutions.bbb.description')}</p>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="p-2 bg-blue-500/20 rounded-lg mr-4">
                  <Users className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h5 className="text-lg font-medium text-white mb-2">{t('solutions.bbb.features.classroom.title')}</h5>
                  <p className="text-blue-100">{t('solutions.bbb.features.classroom.description')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="p-2 bg-blue-500/20 rounded-lg mr-4">
                  <Globe className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h5 className="text-lg font-medium text-white mb-2">{t('solutions.bbb.features.advanced.title')}</h5>
                  <p className="text-blue-100">{t('solutions.bbb.features.advanced.description')}</p>
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              className="mt-8 w-full bg-white text-blue-900 hover:bg-blue-50"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('solutions.bbb.cta')}
            </Button>
          </div>
        </div>

        {/* Jitsi Meet */}
        <div className="relative group">
          <div className="absolute inset-0.5 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
          <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
            <div className="flex items-center mb-6">
              <Video className="h-8 w-8 text-indigo-400 mr-3" />
              <h4 className="text-xl font-semibold text-white">{t('solutions.jitsi.title')}</h4>
            </div>
            <p className="text-blue-100 mb-6">{t('solutions.jitsi.description')}</p>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="p-2 bg-indigo-500/20 rounded-lg mr-4">
                  <Shield className="h-5 w-5 text-indigo-400" />
                </div>
                <div>
                  <h5 className="text-lg font-medium text-white mb-2">{t('solutions.jitsi.features.security.title')}</h5>
                  <p className="text-blue-100">{t('solutions.jitsi.features.security.description')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="p-2 bg-indigo-500/20 rounded-lg mr-4">
                  <MessageSquare className="h-5 w-5 text-indigo-400" />
                </div>
                <div>
                  <h5 className="text-lg font-medium text-white mb-2">{t('solutions.jitsi.features.collaboration.title')}</h5>
                  <p className="text-blue-100">{t('solutions.jitsi.features.collaboration.description')}</p>
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              className="mt-8 w-full bg-white text-indigo-900 hover:bg-indigo-50"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('solutions.jitsi.cta')}
            </Button>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          {
            icon: Globe,
            title: t('features.browser.title'),
            description: t('features.browser.description')
          },
          {
            icon: Shield,
            title: t('features.security.title'),
            description: t('features.security.description')
          },
          {
            icon: Users,
            title: t('features.scalability.title'),
            description: t('features.scalability.description')
          },
          {
            icon: MessageSquare,
            title: t('features.features.title'),
            description: t('features.features.description')
          }
        ].map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div key={index} className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-violet-500 rounded-lg">
                  <Icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-blue-100">{feature.description}</p>

              {/* Decorative elements */}
              <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-4 left-4 w-2 h-2 bg-violet-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MeetingSolution;